import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Logo } from '@/components/ui/Logo'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Sheet } from '@/components/ui/Sheet'
import type { OfferLanguage } from '@/features/quotes/api/useQuote'
import { cn } from '@/lib/cn'
import type {
  OfferPreview,
  OfferPreviewTable,
} from '@/types/domain'

/* The offer, as the client will read it (UX-DR-15).
 *
 * Shown before the send, because the send is irreversible: it freezes a revision,
 * files a PDF and emails a client (FR-71). Up to this point every step of the
 * quote screen shows it as the DESK sees it — internal costs, margin, the
 * vendor behind each line. This is the first and only place the same quote is
 * shown as the CLIENT sees it, and the difference between those two views is
 * exactly what a salesperson needs to check.
 *
 * ── Why this renders the server's document and not the quote ──
 *
 * Everything drawn here comes from GET /quotes/:id/offer, which runs the SAME
 * ComposeOffer the send runs. Nothing on this surface sums a column, formats a
 * currency or translates a label: the rows arrive already formatted by the
 * server's one money rule (UX-DR-30), and the labels arrive in the CLIENT's
 * language because that is the language the document prints in.
 *
 * That is not fussiness. docs.Renderer's own comment says it: "a preview that
 * rendered through a different path than the issued artifact would be a preview
 * that can lie". A sheet that re-derived the totals from QuoteDetail would be a
 * second implementation of the offer, and the first time the two disagreed the
 * user would have approved one document and sent another.
 *
 * The visible consequence, worth expecting rather than reporting as a bug: the
 * table headings and field labels are the CLIENT's language while the sheet's
 * own chrome follows the interface. The document belongs to the client; the
 * chrome belongs to the user.
 *
 * -- THE LANGUAGE SWITCH --
 *
 * WHICH client language, though, is a question this sheet had no way to ask. It
 * fetched `GET /quotes/:id/offer` with no `language`, the server fell back to
 * `defaultOfferLanguage` -- Russian -- and so the preview was Russian for every
 * quote and every reader, whatever the send then did. An English-reading
 * client's offer could not be checked before it left.
 *
 * So the language is a control now, seeded from the interface (the reader's own
 * language is the best guess available -- a quote carries no client-language
 * field) and owned by the PAGE rather than by this sheet, because the send sheet
 * asks the same question and the two must give one answer. Two controls
 * disagreeing about the language would mean previewing one document and sending
 * another, which is precisely what UX-DR-15 exists to prevent.
 *
 * ── DRAFT ──
 *
 * The number is absent on a preview, always — minting belongs to the issuing
 * transaction (AD-12), and docs.Document notes that a renderer which minted would
 * "hand out a number every time somebody hit preview". So the header prints DRAFT
 * where the issued document prints its number, which is the honest statement:
 * this document does not exist yet.
 */

export function OfferPreviewSheet({
  open,
  onClose,
  preview,
  pending,
  error,
  onRetry,
  onSend,
  sendBlockedReason,
  sendPending,
  language,
  onLanguageChange,
}: {
  open: boolean
  onClose: () => void
  preview?: OfferPreview
  pending?: boolean
  error?: Error | null
  onRetry?: () => void
  /** The language the document is composed in: the CLIENT's, not the reader's.
   *  Held by the page so that the send issues in the language checked here. */
  language: OfferLanguage
  onLanguageChange: (language: OfferLanguage) => void
  /** Sending from the preview itself — the point of looking is to then act. */
  onSend?: () => void
  /* Two props rather than the one `sendDisabled` this had, because the two
     states owe the reader different things. A refusal has a cause that can be
     named and acted on; a send already in flight has nothing to say beyond
     "wait", and wording it as a refusal would tell somebody to go fix a quote
     that is at that moment being sent correctly. */
  /** Why the offer may not go out, or undefined when it may — the page's
   *  `sendBlockedReason`, which also knows the reader and the address. Send
   *  is refused exactly when this is set. */
  sendBlockedReason?: string
  /** A send is in flight. Transient, and explained by the button's own label. */
  sendPending?: boolean
}) {
  const { t } = useTranslation()

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('quotes.preview.title')}
      /* Wide because this is the one sheet holding a DOCUMENT rather than a
         form: the cargo and charges tables carry the renderer's own column
         weights, and at line-editor width those weights resolve to columns
         narrow enough to wrap a description or a dimension triple mid-value. */
      size="wide"
      footer={
        onSend ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('quotes.preview.close')}
            </Button>
            <span className="flex-1" />
            {/* The send stays reachable from here, so checking the document and
                acting on it are one motion rather than a sheet the user has to
                dismiss before finding the button again. It is the same gate as
                the footer's: the SERVER decides sendability (FR-3), and this
                button only reflects it.

                WHETHER it refuses is the page's answer, which knows the
                reader and the address as well as the quote. The WORDS come
                from `preview.refusal` when the preview carries one — the
                server's own, and the same sentence as the banner above the
                document, deliberately: two different wordings for one
                refusal, a scroll apart, would read as two separate problems.
                Without one (a reader who may not see the client's contacts,
                a client with no address, a preview still loading) the page's
                sentence stands. */}
            <Button
              onClick={onSend}
              disabled={sendPending}
              blockedReason={
                sendBlockedReason
                  ? preview?.refusal
                    ? t(refusalKey(preview.refusal))
                    : sendBlockedReason
                  : undefined
              }
            >
              {t('quotes.actions.sendOffer')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {/* ABOVE the document and outside it, because it is the reader's control
          rather than part of the page a client receives -- and it stays put
          while the document under it refetches, so switching the language does
          not make the control being used disappear into a skeleton. */}
      <div className="border-line-hair mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b pb-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="asa-eyebrow">{t('quotes.preview.language')}</span>
          <span className="text-text-muted text-[12px] leading-snug font-light">
            {t('quotes.preview.languageHint')}
          </span>
        </div>
        <SegmentedControl
          options={OFFER_LANGUAGES}
          value={language}
          onChange={onLanguageChange}
          label={t('quotes.preview.language')}
        />
      </div>

      {pending ? (
        <div
          className="flex flex-col gap-3"
          aria-busy="true"
          aria-label={t('quotes.preview.loading')}
        >
          <Skeleton className="h-16 w-full rounded-md" />
          <Skeleton className="h-32 w-full rounded-md" />
          <Skeleton className="h-20 w-full rounded-md" />
        </div>
      ) : error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : preview ? (
        <OfferDocument preview={preview} />
      ) : null}
    </Sheet>
  )
}

/* The two languages this company writes to clients in, matching the server's own
   pair (`quote.offerLabels`, `mail.Language`). Labelled with each language's own
   name rather than through the catalogue: the reader is choosing what the CLIENT
   reads, and "English" / "Английский" moving with the interface would invite
   reading this as the interface setting it is not. */
export const OFFER_LANGUAGES = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
] as const satisfies readonly { value: OfferLanguage; label: string }[]

function OfferDocument({ preview }: { preview: OfferPreview }) {
  const { t } = useTranslation()

  return (
    /* A page, not a form. White, bounded, with the document's own hierarchy —
       so what the user checks looks like the artifact rather than like the
       screen that produced it. */
    <article className="flex flex-col gap-4 sm:gap-6">
      {/* The refusal rides ABOVE the document: the preview below it is accurate,
          and the offer still cannot go. Saying both at once is the point — a
          sheet that hid the document until it was sendable would withhold it
          exactly when somebody is trying to see what is wrong. */}
      {preview.refusal ? (
        <Banner tone="attention" title={t('quotes.preview.blockedTitle')}>
          {t(refusalKey(preview.refusal))}
        </Banner>
      ) : null}

      {/* The letterhead, because the artifact has one -- a preview without it
          shows the reader a different page from the one the client opens.
          `platform/docs` draws the ISSUING ENTITY's own mark when it has
          uploaded one and this group lockup when it has not, which is every
          entity today, so this is what a client actually receives.

          No entity name under it, unlike the PDF: the preview's Document is
          composed WITHOUT branding on purpose -- branding belongs to the
          issuing transaction (AD-12, FR-64) -- so the wire has no name to
          print. Inventing one here would be this sheet guessing at a fact the
          send resolves. */}
      <Logo variant="lockup-horizontal-blue" height={30} />

      <header className="border-line flex flex-wrap items-start justify-between gap-3 border-b pb-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-text-heading text-[17px] leading-none font-semibold tracking-[-0.3px]">
            {/* No number on a preview — DRAFT is what the PDF prints too. */}
            {preview.number ?? t('quotes.preview.draft')}
          </span>
          <span className="text-text-muted text-[12.5px] font-light">
            {t('quotes.preview.revision', { label: preview.label })} ·{' '}
            {preview.date}
          </span>
        </div>
        <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
          <span className="text-text-heading text-[13px]">
            {preview.recipient.name}
          </span>
          {preview.recipient.address ? (
            <span className="text-text-muted text-[12px] leading-snug font-light">
              {preview.recipient.address}
            </span>
          ) : null}
        </div>
      </header>

      {/* The header block: references, dates, terms. Labels are the DOCUMENT's,
          in the client's language — see the note at the top of this file.

          Two columns of label/value pairs, three once there is room for them:
          past ~56rem a two-column grid stretches each pair until the distance
          between a label and its value is doing the reading. */}
      {preview.fields?.length ? (
        <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {preview.fields.map((field) => (
            <div key={field.label} className="flex items-baseline gap-3">
              <dt className="text-text-muted shrink-0 text-[12px] font-light">
                {field.label}
              </dt>
              <dd className="text-text-body min-w-0 flex-1 text-[12.5px]">
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {preview.tables?.map((table, index) => (
        <DocumentTable key={`${table.title}-${index}`} table={table} />
      ))}

      {preview.totals?.length ? (
        <dl className="border-line ml-auto flex w-full max-w-[280px] flex-col border-t pt-2 sm:max-w-[340px]">
          {preview.totals.map((total, index) => (
            <div
              key={total.label}
              className="flex items-baseline justify-between gap-4 py-1"
            >
              <dt
                className={cn(
                  'text-[12.5px]',
                  // The last total is the figure the client is held to.
                  index === (preview.totals?.length ?? 0) - 1
                    ? 'text-text-heading font-medium'
                    : 'text-text-muted font-light',
                )}
              >
                {total.label}
              </dt>
              <dd
                className={cn(
                  'asa-num text-[13px]',
                  index === (preview.totals?.length ?? 0) - 1
                    ? 'text-text-heading font-semibold'
                    : 'text-text-body',
                )}
              >
                {total.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* Keyed by position as well as title, because a disclaimer has NO title
          — 0032 keeps the editor's filing label off the client's page — so a
          quote with three of them would otherwise hand React three identical
          keys. */}
      {preview.blocks?.map((block, index) => (
        <section
          key={`${block.title}-${index}`}
          className="flex flex-col gap-1"
        >
          <h3 className="asa-eyebrow">{block.title}</h3>
          {/* whitespace-pre-line: the server composes these as paragraphs with
              real newlines — a not-included list is one string with line breaks,
              not an array — and collapsing them would run the list together. */}
          <p className="text-text-body text-[12.5px] leading-snug font-light whitespace-pre-line">
            {block.body}
          </p>
        </section>
      ))}

      {preview.signatureBlock ? (
        <div className="border-line-hair mt-2 flex flex-col gap-6 border-t pt-4">
          <span className="text-text-muted text-[12px] font-light">
            {t('quotes.preview.signature')}
          </span>
          <span className="border-line-hair w-48 border-b" />
        </div>
      ) : null}
    </article>
  )
}

function DocumentTable({ table }: { table: OfferPreviewTable }) {
  const columns = table.columns ?? []
  const rows = table.rows ?? []
  if (rows.length === 0) return null

  // The renderer's widths are relative weights, so they are turned into
  // percentages of this table rather than pixels — the same proportions the PDF
  // lays out, at whatever width the sheet happens to be.
  const totalWidth = columns.reduce((sum, column) => sum + (column.width || 1), 0)

  return (
    <section className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="asa-eyebrow">{table.title}</h3>
        {/* The currency, stated once for the whole table — the rows below it
            carry bare figures, so dropping this leaves numbers with no
            currency anywhere on the sheet. */}
        {table.caption ? (
          <span className="text-text-muted text-[11px] font-light">
            {table.caption}
          </span>
        ) : null}
      </div>
      {/* Scrolls inside itself: a charges table with five columns must not push
          the sheet sideways. */}
      <div className="asa-scroll min-w-0 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              {columns.map((column) => (
                <th
                  key={column.header}
                  scope="col"
                  className={cn(
                    'asa-eyebrow h-8 px-2 first:pl-0 last:pr-0',
                    column.numeric && 'text-right',
                  )}
                  style={{
                    width: `${((column.width || 1) / totalWidth) * 100}%`,
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  'border-line-hair border-b last:border-b-0',
                  row.emphasis && 'bg-surface-sunken',
                )}
              >
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={cn(
                      'px-2 py-1.5 text-[12.5px] first:pl-0 last:pr-0',
                      columns[cellIndex]?.numeric && 'asa-num text-right',
                      row.emphasis
                        ? 'text-text-heading font-semibold'
                        : 'text-text-body',
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.note ? (
        <p className="text-text-muted text-[11.5px] leading-snug font-light">
          {table.note}
        </p>
      ) : null}
    </section>
  )
}

/** The i18n key for a server refusal token (AD-27: the wire sends tokens).
 *
 *  Returns the KEY rather than the translated sentence, so the caller does the
 *  `t()` at the call site. Passing `t` in as a parameter instead makes its typed
 *  return generic over the key union, which the catalogue's own types then cannot
 *  collapse to `string` — TS2589, "type instantiation is excessively deep".
 *
 *  The tokens are the exact strings quote/revision.go declares — camelCase, not
 *  kebab. An unrecognised one falls back to the generic line rather than printing
 *  the token at somebody: a refusal added server-side should read as "not
 *  sendable yet", never as `noCargo`. */
function refusalKey(refusal: string) {
  switch (refusal) {
    case 'noCargo':
      return 'quotes.preview.blockedNoCargo' as const
    case 'notDraft':
      return 'quotes.preview.blockedNotDraft' as const
    case 'checklistOpen':
      return 'quotes.preview.blockedChecklist' as const
    case 'noRecipient':
      return 'quotes.preview.blockedNoRecipient' as const
    default:
      return 'quotes.preview.blockedGeneric' as const
  }
}
